import {
  BarChartOutlined,
  CarOutlined,
  DashboardOutlined,
  DesktopOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  GiftOutlined,
  IdcardOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  ShopOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { AdminRole } from '@goelectric/shared-types';
import type { ReactNode } from 'react';

/** Ítem del menú lateral con los roles que habilitan su sección. */
export interface NavItem {
  key: string;
  label: string;
  icon: ReactNode;
  /** Roles habilitados: espejo de `x-goelectric-roles` del contrato para la sección. */
  roles: readonly AdminRole[];
}

/**
 * Grupo del menú con encabezado (antd `type: 'group'`): agrupa hojas bajo un
 * título, sin ser navegable. Si el rol no ve ninguna hoja, el grupo se omite por
 * completo (nunca queda vacío ni roto).
 */
export interface NavGroup {
  type: 'group';
  key: string;
  label: string;
  children: NavItem[];
}

/**
 * Separador visual entre bloques del menú (antd `{ type: 'divider' }`). No es
 * navegable ni tiene roles: es decoración de layout. `visibleMenuEntries` lo
 * elimina si queda huérfano tras el filtrado por rol (al inicio, al final, entre
 * dos dividers o colgando de un bloque omitido).
 */
export interface NavDivider {
  type: 'divider';
  /** Identificador estable para las listas de React (antd no lo usa para navegar). */
  key: string;
}

/** Entrada del menú: hoja navegable, grupo con encabezado o divider de bloque. */
export type NavEntry = NavItem | NavGroup | NavDivider;

/** `true` si la entrada es una hoja navegable. */
export function isNavItem(entry: NavEntry): entry is NavItem {
  return !('type' in entry);
}

/** `true` si la entrada es un grupo con encabezado. */
export function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'type' in entry && entry.type === 'group';
}

/** `true` si la entrada es un divider de bloque. */
export function isNavDivider(entry: NavEntry): entry is NavDivider {
  return 'type' in entry && entry.type === 'divider';
}

/** El NOC y el laboratorio comparten roles (contrato: `/noc/*` y `/noc/sim/*`). */
export const NOC_SECTION_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3'];

/** Aliados: espejo de `x-goelectric-roles` de `GET /admin/allies` y `/admin/allies/{id}`. */
export const ALIADOS_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3', 'MARKETING'];

/**
 * Escritura de aliados: el contrato la declara solo para `SUPER_ADMIN` (las lecturas son
 * de los tres roles). Gobierna la ruta `/aliados/nuevo` y la visibilidad de las acciones
 * de alta, para no ofrecer un botón que terminaría en 403.
 */
export const ALIADOS_WRITE_ROLES: readonly AdminRole[] = ['SUPER_ADMIN'];

/** `true` si el rol puede crear/editar aliados. */
export function canWriteAliados(role: AdminRole | undefined): boolean {
  return role !== undefined && ALIADOS_WRITE_ROLES.includes(role);
}

/**
 * DOOH — medios y emisión global: espejo de `x-goelectric-roles` de
 * `POST/GET /dooh/media` y `GET/PUT /dooh/broadcast` (SUPER_ADMIN / MARKETING).
 */
export const DOOH_MEDIA_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'MARKETING'];

/** DOOH — pantallas: espejo de `x-goelectric-roles` de `/dooh/screens/*` (SUPER_ADMIN / SOPORTE_N3). */
export const DOOH_SCREENS_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3'];

/** `true` si el rol puede gestionar la biblioteca y la emisión global DOOH. */
export function canManageDoohMedia(role: AdminRole | undefined): boolean {
  return role !== undefined && DOOH_MEDIA_ROLES.includes(role);
}

/** `true` si el rol puede ver/configurar pantallas DOOH. */
export function canManageDoohScreens(role: AdminRole | undefined): boolean {
  return role !== undefined && DOOH_SCREENS_ROLES.includes(role);
}

/**
 * Nombre visible de la sección DOOH (el acrónimo no se muestra al usuario).
 *
 * "Pantallas Digitales" describe el módulo completo (biblioteca de medios →
 * emisión global → pantallas) y es el nombre que usa el cliente. Cambia este
 * único valor para renombrar la sección en todo el Admin Web (menú y títulos).
 * Los identificadores internos (`features/dooh`, rutas `/dooh/*`, componentes y
 * servicios) se mantienen estables a propósito.
 */
export const DOOH_SECTION_LABEL = 'Pantallas Digitales';

/** Locaciones: espejo de `x-goelectric-roles` de `GET /admin/locations` y `/admin/locations/{id}`. */
export const LOCACIONES_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3', 'MARKETING'];

/**
 * Escritura de locaciones: el contrato la declara solo para `SUPER_ADMIN` (las lecturas
 * son de los tres roles). Gobierna la visibilidad de las acciones de alta, para no
 * ofrecer un botón que terminaría en 403.
 */
export const LOCACIONES_WRITE_ROLES: readonly AdminRole[] = ['SUPER_ADMIN'];

/** `true` si el rol puede crear/editar locaciones. */
export function canWriteLocaciones(role: AdminRole | undefined): boolean {
  return role !== undefined && LOCACIONES_WRITE_ROLES.includes(role);
}

/**
 * Tarifas: catálogo global de plataforma (ADR 0046). El contrato NO restringe la
 * lectura por rol (cualquier usuario autenticado del backoffice puede consultar
 * `/api/v1/tariffs`), por lo que la sección se expone a todos los roles. `ALIADO_ADMIN`
 * solo consulta; las escrituras (`POST`/`PATCH`/`DELETE`) declaran
 * `x-goelectric-roles: [SUPER_ADMIN]`.
 */
export const TARIFAS_ROLES: readonly AdminRole[] = [
  'SUPER_ADMIN',
  'SOPORTE_N3',
  'MARKETING',
  'ALIADO_ADMIN',
  'CONTADOR',
];

/** Escritura de tarifas: solo `SUPER_ADMIN` según el contrato. */
export const TARIFAS_WRITE_ROLES: readonly AdminRole[] = ['SUPER_ADMIN'];

/** `true` si el rol puede crear/editar/eliminar tarifas. */
export function canWriteTarifas(role: AdminRole | undefined): boolean {
  return role !== undefined && TARIFAS_WRITE_ROLES.includes(role);
}

/**
 * Cargadores: el contrato declara `x-goelectric-roles: [SUPER_ADMIN]` para
 * `GET /admin/stations` (decisión del Tech Lead). Solo `SUPER_ADMIN` ve la sección.
 */
export const CARGADORES_ROLES: readonly AdminRole[] = ['SUPER_ADMIN'];

/**
 * `true` si el rol puede listar cargadores. Se usa fuera de la sección de Cargadores para
 * gatear consumidores embebidos (p. ej. el panel de cargadores en el detalle de Ubicación):
 * la API de cargadores es solo `SUPER_ADMIN`, así que un rol de lectura de ubicaciones no
 * debe ni pedirla (recibiría 403).
 */
export function canViewCargadores(role: AdminRole | undefined): boolean {
  return role !== undefined && CARGADORES_ROLES.includes(role);
}

/**
 * Clientes: espejo de `x-goelectric-roles` de `GET /admin/customers` y
 * `/admin/customers/{id}`. `ALIADO_ADMIN` es solo lectura y el backend le acota el
 * listado a los clientes de su propio aliado.
 */
export const CLIENTES_ROLES: readonly AdminRole[] = [
  'SUPER_ADMIN',
  'SOPORTE_N3',
  'MARKETING',
  'ALIADO_ADMIN',
];

/**
 * Escritura de clientes: el contrato la declara solo para `SUPER_ADMIN`/`SOPORTE_N3`
 * (`POST`, `PATCH` y el ajuste de saldo). Gobierna la ruta `/clientes/nuevo` y la
 * visibilidad de las acciones, para no ofrecer un botón que terminaría en 403.
 */
export const CLIENTES_WRITE_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3'];

/**
 * Reportes (US-2.8): roles administrativos del backoffice. El catálogo NO se
 * gatea acá: el backend devuelve solo los reportes visibles para el rol. Este
 * arreglo solo evita ofrecer la sección a roles sin acceso administrativo;
 * `ALIADO_ADMIN` queda afuera porque ningún reporte le da permiso (abriría una
 * pantalla vacía).
 */
export const REPORTES_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3', 'MARKETING'];

/** `true` si el rol puede crear/editar clientes y ajustar saldo. */
export function canWriteClientes(role: AdminRole | undefined): boolean {
  return role !== undefined && CLIENTES_WRITE_ROLES.includes(role);
}

/**
 * Flotas corporativas (US-2.3, ADR 0047): espejo de `x-goelectric-roles` de
 * `GET /admin/fleets` y `/admin/fleets/{fleetId}`. Son clientes de plataforma, así que
 * `MARKETING` y `ALIADO_ADMIN` no tienen acceso.
 */
export const FLOTAS_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3'];

/**
 * Escritura de flotas: mismo par que la lectura (`SUPER_ADMIN`/`SOPORTE_N3`), igual que
 * los endpoints de alta/edición y de conductores/vehículos.
 */
export const FLOTAS_WRITE_ROLES: readonly AdminRole[] = ['SUPER_ADMIN', 'SOPORTE_N3'];

/** `true` si el rol puede crear/editar flotas y gestionar sus conductores/vehículos. */
export function canWriteFlotas(role: AdminRole | undefined): boolean {
  return role !== undefined && FLOTAS_WRITE_ROLES.includes(role);
}

/**
 * Packs de Energía (US-2.4): catálogo global de plataforma, por lo que la sección
 * se expone a los cinco roles de catálogo. El backend restringe `status=ALL` y
 * `validAt` a SUPER_ADMIN/SOPORTE_N3/MARKETING; ALIADO_ADMIN y CONTADOR solo pueden
 * leer `ACTIVE` dentro de vigencia. La feature `packs` ajusta el filtro a ese alcance.
 * Las escrituras (`POST`/`PATCH`/`DELETE`) son solo `SUPER_ADMIN`.
 */
export const PACKS_ROLES: readonly AdminRole[] = [
  'SUPER_ADMIN',
  'SOPORTE_N3',
  'MARKETING',
  'ALIADO_ADMIN',
  'CONTADOR',
];

/** Escritura de packs: solo `SUPER_ADMIN` según el contrato. */
export const PACKS_WRITE_ROLES: readonly AdminRole[] = ['SUPER_ADMIN'];

/** `true` si el rol puede crear/editar/eliminar packs. */
export function canWritePacks(role: AdminRole | undefined): boolean {
  return role !== undefined && PACKS_WRITE_ROLES.includes(role);
}

/**
 * Membresías (US-2.4): catálogo global de plataforma. La lectura se expone a los
 * cinco roles de catálogo; el backend restringe `status=ALL|INACTIVE` y el detalle
 * de una membresía inactiva a SUPER_ADMIN/SOPORTE_N3/MARKETING. ALIADO_ADMIN y
 * CONTADOR solo leen `ACTIVE`. Las escrituras son solo `SUPER_ADMIN`.
 */
export const MEMBERSHIPS_ROLES: readonly AdminRole[] = [
  'SUPER_ADMIN',
  'SOPORTE_N3',
  'MARKETING',
  'ALIADO_ADMIN',
  'CONTADOR',
];

/** Escritura de membresías: solo `SUPER_ADMIN` según el contrato. */
export const MEMBERSHIPS_WRITE_ROLES: readonly AdminRole[] = ['SUPER_ADMIN'];

/** `true` si el rol puede crear/editar/eliminar membresías. */
export function canWriteMemberships(role: AdminRole | undefined): boolean {
  return role !== undefined && MEMBERSHIPS_WRITE_ROLES.includes(role);
}

/**
 * Hojas de la sección de pantallas digitales, en el orden del flujo operativo
 * **biblioteca de medios → emisión global → pantallas**. Cada hoja conserva su
 * propio RBAC (medios/emisión para Marketing; pantallas para Soporte N3).
 */
export const DOOH_NAV_ITEMS: NavItem[] = [
  {
    key: '/dooh/medios',
    label: 'Biblioteca de medios',
    icon: <PictureOutlined />,
    roles: DOOH_MEDIA_ROLES,
  },
  {
    key: '/dooh/emision',
    label: 'Emisión global',
    icon: <PlayCircleOutlined />,
    roles: DOOH_MEDIA_ROLES,
  },
  {
    key: '/dooh/pantallas',
    label: 'Pantallas',
    icon: <DesktopOutlined />,
    roles: DOOH_SCREENS_ROLES,
  },
];

/**
 * Árbol del menú lateral en tres bloques separados por dividers:
 * **Operación** (NOC, Laboratorio) — **Catálogo** (Aliados, Ubicaciones, Cargadores,
 * Clientes, Flotas, Tarifas, Packs, Membresías) — **Pantallas Digitales** (grupo con
 * sus tres hojas, siempre al final).
 * `visibleMenuEntries` filtra este árbol por rol y limpia los dividers huérfanos.
 */
export const MENU_ENTRIES: NavEntry[] = [
  // Bloque 1 — Operación.
  { key: '/noc', label: 'NOC', icon: <DashboardOutlined />, roles: NOC_SECTION_ROLES },
  {
    key: '/laboratorio',
    label: 'Laboratorio',
    icon: <ExperimentOutlined />,
    roles: NOC_SECTION_ROLES,
  },
  { type: 'divider', key: 'divider-operacion-catalogo' },
  // Bloque 2 — Catálogo.
  { key: '/aliados', label: 'Aliados', icon: <ShopOutlined />, roles: ALIADOS_ROLES },
  { key: '/ubicaciones', label: 'Ubicaciones', icon: <EnvironmentOutlined />, roles: LOCACIONES_ROLES },
  { key: '/cargadores', label: 'Cargadores', icon: <ThunderboltOutlined />, roles: CARGADORES_ROLES },
  { key: '/clientes', label: 'Clientes', icon: <TeamOutlined />, roles: CLIENTES_ROLES },
  { key: '/flotas', label: 'Flotas', icon: <CarOutlined />, roles: FLOTAS_ROLES },
  { key: '/tarifas', label: 'Tarifas', icon: <DollarOutlined />, roles: TARIFAS_ROLES },
  { key: '/packs', label: 'Packs', icon: <GiftOutlined />, roles: PACKS_ROLES },
  {
    key: '/membresias',
    label: 'Membresías',
    icon: <IdcardOutlined />,
    roles: MEMBERSHIPS_ROLES,
  },
  { key: '/reportes', label: 'Reportes', icon: <BarChartOutlined />, roles: REPORTES_ROLES },
  { type: 'divider', key: 'divider-catalogo-pantallas' },
  // Bloque 3 — Pantallas Digitales.
  { type: 'group', key: 'dooh', label: DOOH_SECTION_LABEL, children: DOOH_NAV_ITEMS },
];

/**
 * Lista plana de hojas del menú (destino inicial y compatibilidad), en el orden
 * visual: primero las hojas sueltas y, al final, las hojas del grupo de pantallas.
 * Ignora grupos y dividers.
 */
export const MENU_ITEMS: NavItem[] = MENU_ENTRIES.flatMap((entry) => {
  if (isNavGroup(entry)) return entry.children;
  return isNavItem(entry) ? [entry] : [];
});

/**
 * Elimina los dividers que quedan huérfanos tras el filtrado por rol. Un divider
 * es válido solo si separa dos bloques con contenido visible: se descartan los
 * iniciales, los finales, los consecutivos y los que colgaban de un bloque omitido
 * (que colapsan a inicio/fin/consecutivos al desaparecer su contenido).
 */
function stripOrphanDividers(entries: NavEntry[]): NavEntry[] {
  const result: NavEntry[] = [];
  for (const entry of entries) {
    if (isNavDivider(entry)) {
      const previous = result[result.length - 1];
      // Sin contenido previo (divider inicial) o pegado a otro divider: se omite.
      if (previous === undefined || isNavDivider(previous)) continue;
      result.push(entry);
      continue;
    }
    result.push(entry);
  }
  // Un divider al final no separa nada (bloque siguiente omitido): se elimina.
  let last = result[result.length - 1];
  while (last !== undefined && isNavDivider(last)) {
    result.pop();
    last = result[result.length - 1];
  }
  return result;
}

/**
 * Filtra el árbol del menú según el rol; ocultar el ítem es UX, el guard de ruta
 * es la seguridad. Un grupo sin hojas visibles para el rol **se omite entero** y
 * un grupo con un **único** hijo visible **se aplana**: ese hijo se devuelve como
 * hoja de primer nivel (sin encabezado), porque un encabezado para una sola hoja
 * no agrupa nada. Los dividers que queden huérfanos por el filtrado se eliminan.
 */
export function visibleMenuEntries(
  role: AdminRole | undefined,
  entries: NavEntry[] = MENU_ENTRIES,
): NavEntry[] {
  if (!role) return [];
  const filtered = entries.flatMap<NavEntry>((entry) => {
    if (isNavDivider(entry)) return [entry];
    if (isNavGroup(entry)) {
      const children = entry.children.filter((child) => child.roles.includes(role));
      // Sin hijos visibles el grupo se omite entero.
      if (children.length === 0) return [];
      // Con un único hijo visible el grupo se aplana: el hijo pasa a ser una hoja
      // de primer nivel (con su ícono) y se descarta el encabezado, que ya no agrupa
      // nada. Con dos o más hijos se conserva el encabezado (antd `type: 'group'`).
      if (children.length === 1) return children;
      return [{ ...entry, children }];
    }
    return entry.roles.includes(role) ? [entry] : [];
  });
  return stripOrphanDividers(filtered);
}

/** Filtra las hojas del menú según el rol (lista plana, sin grupos). */
export function visibleMenuItems(role: AdminRole | undefined, items: NavItem[] = MENU_ITEMS): NavItem[] {
  return role ? items.filter((item) => item.roles.includes(role)) : [];
}

/**
 * Primer destino visible para el rol: el redirect del `index` no debe aterrizar en
 * una sección que el rol no puede ver (evita el 403 con sidebar vacío). Si no hay
 * ningún ítem visible, cae en `/noc` (el guard mostrará el 403).
 */
export function defaultLandingPath(role: AdminRole | undefined, items: NavItem[] = MENU_ITEMS): string {
  return visibleMenuItems(role, items)[0]?.key ?? '/noc';
}
